import React, { useEffect, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import  {getHPCReportForPDF} from '../../repositories/hpc/hpcRepo';
import DownloadButton from '../common/DownloadButton';
import { LuArrowLeft } from 'react-icons/lu';
import LoadingMessage from '../common/LoadingMessage';
import ErrorWidget from '../common/ErrorWidget';
import ReactMarkdown from 'react-markdown';
import PDFPageContainer from '../utils/PDFPageContainer';
import '../utils/PDFStyles.css';
import StudentProfileHeader from './StudentProfileHeader';
import OverallProgress from './OverallProgress';
import SectionCard from './SectionCard';

const getAge = dob => {
  if (!dob) return '';
  const birth = new Date(dob);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

// Function to convert a name to title case
const toTitleCase = (name) => {
  if (!name) return '';
  
  return name
    .split(' ')
    .map(word => {
      if (word.length === 0) return word;
      return word[0].toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

const HPCReportWindow = ({ reportId, onBack }) => {
  const { colors } = useTheme();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debugMode, setDebugMode] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  // Add a debugging effect to check if the OverallProgress component is in the DOM
  useEffect(() => {
    if (!loading && report) {
      // Use a small timeout to ensure the component has been rendered
      setTimeout(() => {
        const overallProgressElem = document.querySelector('.section-card');
       
      }, 1000);
    }
  }, [loading, report]);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        if (!reportId) throw new Error('Report ID is required');
        setLoading(true);
        const reportData = await getHPCReportForPDF(reportId);
        if (reportData.success) {
          setReport(reportData.value);
        } else {
          setError(reportData.message);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [reportId]);


  // Inside the dashboard (onBack set) the shell gives the padding; the public /hpc-report link fills the screen
  const backLink = onBack && (
    <button
      type="button"
      onClick={onBack}
      className="mb-4 inline-flex items-center gap-1.5 rounded text-[13px] font-medium text-ui-ink-2 transition-colors hover:text-ui-ink"
    >
      <LuArrowLeft aria-hidden className="size-4" />
      HPC reports
    </button>
  );

  if (loading) return <LoadingMessage message="Loading the progress card…" className="min-h-[50vh]" />;
  if (error) return <div className="mx-auto max-w-lg py-10">{backLink}<ErrorWidget error={{ message: error }} title="Could not open the progress card" /></div>;
  if (!report) return <div className="py-10 text-center text-sm text-ui-ink-2">{backLink}No report data available.</div>;

  return (
    <div className={onBack ? '' : 'min-h-screen bg-neutral-100 py-6'}>
      {backLink}

      <PDFPageContainer className="bg-white shadow-card">
        <StudentProfileHeader report={report} />
        
        <OverallProgress summary={report.overallSummary || 'No summary available for this student yet.'} />
        
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 report-content-wrapper">
          {report.sections?.map(section => {
            return (
              <SectionCard key={section.id || section.title} section={section} />
            );
          })}
        </div>
      </PDFPageContainer>
    </div>
  );
};

export default HPCReportWindow;
